import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Favorites & Compare context.
 *
 * Both features share the same backbone (a Set of property IDs persisted to
 * localStorage) so we keep them in one provider. Favorites: unlimited; the
 * heart toggle pins a property for later. Compare: capped at 3 because a
 * side-by-side comparison wider than that no longer fits on a phone.
 *
 * No backend involvement — this is purely client-side personalisation. That
 * keeps the lead capture flow private (we don't want to gate "save for later"
 * behind a login) and avoids GDPR concerns around storing visitor activity.
 */

const FAV_KEY = 'casaothers_favorites';
const CMP_KEY = 'casaothers_compare';
const COMPARE_MAX = 3;

const FavoritesContext = createContext(null);

const safeRead = (key) => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => new Set(safeRead(FAV_KEY)));
  const [compare, setCompare] = useState(() => new Set(safeRead(CMP_KEY)));

  // Persist on every change. Two effects rather than one merged effect so
  // toggling compare doesn't re-serialise the (much larger) favorites set.
  useEffect(() => { localStorage.setItem(FAV_KEY, JSON.stringify([...favorites])); }, [favorites]);
  useEffect(() => { localStorage.setItem(CMP_KEY, JSON.stringify([...compare])); }, [compare]);

  const toggleFavorite = useCallback((id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const isFavorite = useCallback((id) => favorites.has(id), [favorites]);

  const toggleCompare = useCallback((id) => {
    let added = null; // true = added, false = removed, null = at cap
    setCompare(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        added = false;
      } else if (next.size < COMPARE_MAX) {
        next.add(id);
        added = true;
      }
      return next;
    });
    return added;
  }, []);

  const isInCompare = useCallback((id) => compare.has(id), [compare]);
  const clearCompare = useCallback(() => setCompare(new Set()), []);

  const value = {
    favorites: [...favorites],
    favoritesCount: favorites.size,
    isFavorite,
    toggleFavorite,
    compare: [...compare],
    compareCount: compare.size,
    compareMax: COMPARE_MAX,
    isInCompare,
    toggleCompare,
    clearCompare,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used inside FavoritesProvider');
  return ctx;
};
